import { useRef } from 'react'
import type { WireArticleBlock } from '../../../api/types'
import { useVisualEngagementContext } from '../common/VisualEngagementContext'
import { useVisualInViewTracking } from '../common/visualCollectionTracking'
import { PortionMark } from '../common/PortionMark'
import { InlineContent } from '../common/InlineContent'
import { getTableVariantStyles, TABLE_BG_CLASSES } from '../../tableVariants'

type Block = Extract<WireArticleBlock, { type: 'table' }>

const ALIGN_CLASS = { left: 'text-left', center: 'text-center', right: 'text-right' }

export function TableBlock({ block }: { block: Block }) {
  const engagement = useVisualEngagementContext()
  const blockRef = useRef<HTMLDivElement>(null)
  const headerRows = block.rows.filter(r => r.rowType === 'header')
  const bodyRows   = block.rows.filter(r => r.rowType === 'body')
  const v = getTableVariantStyles(block.variant)

  useVisualInViewTracking(engagement, blockRef, block.id, 'TABLE')

  return (
    <div ref={blockRef}>
      <PortionMark classification={block.classification} disseminationControls={block.disseminationControls} />
      <div className="overflow-x-auto">
        {block.caption && (
          <p className="border-b border-line px-4 py-2 text-xs italic text-fg-secondary">
            {block.caption}
          </p>
        )}
        <table className={`text-sm ${v.table}`}>
          {headerRows.length > 0 && (
            <thead>
              {headerRows.map((row, ri) => (
                <tr key={ri} className={v.headerTr}>
                  {row.cells.map((cell, ci) => (
                    (() => {
                      const headerAlign = cell.align ?? block.columnStyles[ci]?.align
                      return (
                    <th key={ci} colSpan={cell.colSpan}
                      className={`${v.headerTh(ci)} ${headerAlign ? ALIGN_CLASS[headerAlign] : ''}`}
                    >
                      <InlineContent>{cell.children}</InlineContent>
                    </th>
                      )
                    })()
                  ))}
                </tr>
              ))}
            </thead>
          )}
          <tbody>
            {bodyRows.map((row, ri) => {
              // Row-level bg: highlighted > explicit bg > variant stripe
              const hasRowBg = !!(row.highlighted || row.bg)
              const rowBg = row.highlighted
                ? 'bg-yellow-3'
                : row.bg
                  ? TABLE_BG_CLASSES[row.bg]
                  : v.bodyRowBg(ri)

              return (
                <tr key={ri} className={`${v.bodyTrBase(ri)} ${rowBg}${row.bold ? ' font-bold' : ''}`}>
                  {row.cells.map((cell, ci) => {
                    // Cell-level bg: skip when row already has a bg
                    const colBg = !hasRowBg && block.columnStyles[ci]?.bg
                      ? TABLE_BG_CLASSES[block.columnStyles[ci].bg!]
                      : ''
                    const cellBg = colBg || (!hasRowBg ? v.bodyCellBg(ci) : '')
                    const colAlign = cell.align ?? block.columnStyles[ci]?.align
                    const isRowHeader = ci === 0 && block.hasHeaderColumn

                    return isRowHeader ? (
                      <th key={ci} colSpan={cell.colSpan}
                        className={`${v.bodyRowHeaderThBase()} ${hasRowBg ? rowBg : v.bodyRowHeaderBg()} ${colAlign ? ALIGN_CLASS[colAlign] : ''}`}
                      >
                        <InlineContent>{cell.children}</InlineContent>
                      </th>
                    ) : (
                      <td key={ci} colSpan={cell.colSpan}
                        className={`${v.bodyTdBase()} ${cellBg} ${colAlign ? ALIGN_CLASS[colAlign] : ''}`}
                      >
                        <InlineContent>{cell.children}</InlineContent>
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
